// src/hooks/useUpcomingIncome.ts
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { withForcedLedgerReadMode } from '../utils/ledgerReadMode';
import { authFetch } from '../lib/authFetch';

type UpcomingIncomeTx = {
  _id: string;
  description?: string;
  category: string;
  amount: number;
  date: string;
};

export const useUpcomingIncome = (days: number = 14, tags?: string[]) => {
  const { auth } = useAuth();
  const [upcoming, setUpcoming] = useState<UpcomingIncomeTx[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tagsKey = useMemo(() => JSON.stringify(tags ?? []), [tags]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!auth?.token) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams();
        if (days) qs.set('days', String(days));
        if (tags?.length) qs.set('tags', tags.join(','));

        const r = await authFetch(withForcedLedgerReadMode(`/api/transactions/upcoming-income?${qs.toString()}`), {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!r.ok) throw new Error('Failed to fetch upcoming income');
        const data = await r.json();
                console.log(
          '📥 useUpcomingIncome parsed data:',
          data.map((tx: any) => ({
            id: tx._id,
            desc: tx.description,
            isRecurring: tx.isRecurring,
            nextDueDate: tx.nextDueDate,
            date: tx.date,
            tags: tx.tags,
          }))
        );
        if (!cancelled) setUpcoming(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [auth?.token, days, tagsKey]);

  return { upcoming, loading, error };
};
